import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { 
  Home, 
  ArrowLeft, 
  Search, 
  AlertCircle, 
  FileQuestion, 
  HelpCircle, 
  Map, 
  ExternalLink, 
  Mail, 
  ChevronRight 
} from 'lucide-react';

const NotFound = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 300, damping: 24 }
    }
  };

  const iconVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { type: 'spring', stiffness: 300, damping: 15, delay: 0.2 }
    }
  };

  const pulseVariants = {
    pulse: {
      scale: [1, 1.05, 1],
      opacity: [0.7, 0.9, 0.7],
      transition: {
        duration: 3,
        repeat: Infinity,
        repeatType: 'loop'
      }
    }
  };

  const floatVariants = {
    float: {
      y: [0, -15, 0],
      transition: {
        duration: 6,
        repeat: Infinity,
        repeatType: 'loop',
        ease: 'easeInOut'
      }
    }
  };

  const quickLinks = [
    { name: 'Ana Sayfa', icon: Home, path: '/' },
    { name: 'Yardım Merkezi', icon: HelpCircle, path: '/help-support' },
    { name: 'Site Haritası', icon: Map, path: '/welcome' }
  ];

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center ${
      isDark 
        ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800' 
        : 'bg-gradient-to-br from-gray-50 via-white to-blue-50'
    } px-4 py-12 overflow-hidden`}>
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Grid pattern */}
        <div className={`absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] ${
          isDark ? 'opacity-10' : 'opacity-20'
        }`} />

        {/* Floating circles */}
        <motion.div 
          animate={{ y: [-20, 20], opacity: [0.3, 0.5, 0.3] }} 
          transition={{ duration: 10, repeat: Infinity, repeatType: 'mirror' }} 
          className={`absolute top-20 left-20 w-64 h-64 rounded-full blur-3xl ${
            isDark ? 'bg-blue-900/20' : 'bg-blue-500/10'
          }`}
        />
        <motion.div 
          animate={{ y: [20, -20], opacity: [0.2, 0.4, 0.2] }} 
          transition={{ duration: 15, repeat: Infinity, repeatType: 'mirror', delay: 1 }} 
          className={`absolute bottom-20 right-20 w-80 h-80 rounded-full blur-3xl ${
            isDark ? 'bg-indigo-900/20' : 'bg-indigo-500/10'
          }`}
        />
      </div>

      {/* Floating tech elements for a corporate tech feel */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Binary code */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.05 }}
          transition={{ delay: 1, duration: 2 }}
          className={`absolute top-1/4 left-10 text-xs font-mono ${
            isDark ? 'text-blue-500' : 'text-blue-800'
          }`}
        >
          {Array(10).fill().map((_, i) => (
            <div key={i} className="my-1">
              {Array(20).fill().map((_, j) => (
                <span key={j}>{Math.round(Math.random())}</span>
              ))}
            </div>
          ))}
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.05 }}
          transition={{ delay: 1.5, duration: 2 }}
          className={`absolute bottom-1/4 right-10 text-xs font-mono ${
            isDark ? 'text-indigo-500' : 'text-indigo-800'
          }`}
        >
          {Array(10).fill().map((_, i) => (
            <div key={i} className="my-1">
              {Array(20).fill().map((_, j) => (
                <span key={j}>{Math.round(Math.random())}</span>
              ))}
            </div>
          ))}
        </motion.div>
      </div>

      {/* Main content container */}
      <div className="w-full max-w-7xl mx-auto px-4 z-10 flex flex-col lg:flex-row lg:items-stretch gap-10">
        {/* Left side - Error info */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="flex-1 flex flex-col items-center lg:items-start text-center lg:text-left lg:justify-center"
        >
          {/* Corporate logo/brand */}
          <motion.div 
            variants={itemVariants}
            className="mb-8"
          >
            <div className={`inline-flex items-center px-4 py-2 rounded-full ${
              isDark ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-800'
            } backdrop-blur-sm`}>
              <span className="font-semibold tracking-wider">İŞ TAKİP PLATFORMU</span>
            </div>
          </motion.div>

          {/* Error status badge */}
          <motion.div
            variants={itemVariants}
            className={`mb-2 px-3 py-1 rounded-full text-sm font-medium inline-flex items-center gap-1.5 ${
              isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-800'
            }`}
          >
            <span className="relative flex w-2 h-2">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${
                isDark ? 'bg-red-400' : 'bg-red-500'
              } opacity-75`}></span>
              <span className={`relative inline-flex rounded-full h-2 w-2 ${
                isDark ? 'bg-red-500' : 'bg-red-600'
              }`}></span>
            </span>
            Hata 404
          </motion.div>

          {/* Main heading */}
          <motion.h1
            variants={itemVariants}
            className={`text-6xl sm:text-7xl font-bold leading-tight mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}
          >
            Sayfa <br className="hidden sm:block" />
            <span className={`${
              isDark ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500' 
                     : 'text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600'
            }`}>
              Bulunamadı
            </span>
          </motion.h1>

          {/* Error number - large background */}
          <div className="relative">
            <motion.span 
              initial={{ opacity: 0, scale: 1.5 }}
              animate={{ opacity: 0.07, scale: 1 }}
              transition={{ duration: 1, delay: 0.5 }}
              className={`absolute text-[12rem] font-bold top-0 left-1/2 lg:left-0 transform -translate-x-1/2 lg:-translate-x-0 -translate-y-2/3 lg:-translate-y-1/2 ${
                isDark ? 'text-gray-200' : 'text-gray-900'
              } z-0`}
            >
              404
            </motion.span>
          </div>

          {/* Message */}
          <motion.p 
            variants={itemVariants}
            className={`text-xl max-w-lg mb-8 ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}
          >
            Aradığınız kaynak mevcut değil veya taşınmış olabilir. Sistem yöneticiniz 
            bu sorunu analiz etmek için bilgilendirildi.
          </motion.p>

          {/* Action Buttons */}
          <motion.div 
            variants={itemVariants}
            className="flex flex-col sm:flex-row gap-4 mb-12"
          >
            <motion.button
              whileHover={{ scale: 1.02, boxShadow: "0 10px 20px rgba(0, 0, 0, 0.1)" }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/')}
              className="group flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl text-white bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 font-medium shadow-lg shadow-blue-500/20 transition-all duration-200"
            >
              <motion.span
                initial={{ x: 0 }}
                whileHover={{ x: -3 }}
                className="flex items-center gap-2"
              >
                <Home className="w-5 h-5" />
                <span>Ana Sayfaya Dön</span>
              </motion.span>
              <motion.span
                initial={{ opacity: 0, right: -5 }}
                whileHover={{ opacity: 1, right: -15 }}
                className="opacity-0 group-hover:opacity-100 absolute transition-all duration-200"
              >
                <ChevronRight className="w-5 h-5" />
              </motion.span>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02, boxShadow: "0 10px 20px rgba(0, 0, 0, 0.05)" }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(-1)}
              className={`group flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl ${
                isDark 
                  ? 'bg-white/10 hover:bg-white/15 text-white backdrop-blur-sm border border-white/10' 
                  : 'bg-white hover:bg-gray-50 text-gray-800 border border-gray-200'
              } font-medium shadow-lg transition-all duration-200`}
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              <span>Geri Dön</span>
            </motion.button>
          </motion.div>

          {/* Quick links - mobile visible, desktop hidden */}
          <motion.div
            variants={itemVariants}
            className="w-full lg:hidden"
          >
            <div className={`p-6 rounded-xl ${
              isDark ? 'bg-white/5 backdrop-blur-md border border-white/10' : 'bg-white/80 backdrop-blur-md border border-gray-100'
            } shadow-xl`}>
              <h3 className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Hızlı Erişim
              </h3>
              <ul className="space-y-2">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <button 
                      onClick={() => navigate(link.path)}
                      className={`w-full text-left p-3 rounded-lg flex items-center gap-3 transition-all ${
                        isDark 
                          ? 'hover:bg-white/5 text-gray-300 hover:text-white' 
                          : 'hover:bg-gray-100 text-gray-700 hover:text-gray-900'
                      }`}
                    >
                      <link.icon className="w-5 h-5" />
                      <span>{link.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </motion.div>

        {/* Right side - 3D illustration and help options */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="flex-1 flex flex-col items-center lg:items-end justify-center"
        >
          {/* 3D error illustration */}
          <motion.div 
            variants={iconVariants}
            animate={{ 
              y: [0, -15, 0],
              transition: {
                duration: 6,
                repeat: Infinity,
                repeatType: 'loop',
                ease: 'easeInOut'
              }
            }}
            className="relative mb-12 lg:mb-16"
          >
            <div className="relative">
              {/* Glow effect */}
              <motion.div
                variants={pulseVariants}
                animate="pulse"
                className={`absolute inset-0 rounded-full blur-3xl ${
                  isDark ? 'bg-blue-500/10' : 'bg-blue-500/20'
                }`}
                style={{ width: '100%', height: '100%', top: '0%', left: '0%' }}
              />
              
              {/* Main circle with 3D effect */}
              <div 
                className={`relative w-64 h-64 rounded-full ${
                  isDark 
                    ? 'bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700' 
                    : 'bg-gradient-to-br from-white to-gray-100 border border-gray-200'
                } shadow-2xl flex items-center justify-center`}
                style={{
                  boxShadow: isDark 
                    ? 'inset 10px 10px 20px rgba(255,255,255,0.05), inset -10px -10px 20px rgba(0,0,0,0.5)' 
                    : 'inset 10px 10px 20px rgba(255,255,255,0.8), inset -10px -10px 20px rgba(0,0,0,0.05)'
                }}
              >
                {/* 404 Text */}
                <div className="text-center">
                  <motion.p
                    animate={{ 
                      rotateX: [0, 10, 0, -10, 0],
                      rotateY: [0, 15, 0, -15, 0]
                    }}
                    transition={{ 
                      duration: 10, 
                      repeat: Infinity, 
                      repeatType: 'loop',
                    }}
                    className={`font-bold text-6xl mb-1 ${
                      isDark 
                        ? 'text-transparent bg-clip-text bg-gradient-to-br from-blue-400 via-indigo-400 to-purple-400' 
                        : 'text-transparent bg-clip-text bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600'
                    }`}
                    style={{ 
                      textShadow: isDark 
                        ? '0 2px 10px rgba(79, 70, 229, 0.3)' 
                        : '0 2px 10px rgba(79, 70, 229, 0.2)' 
                    }}
                  >
                    404
                  </motion.p>
                  <motion.p 
                    className={`text-sm font-medium ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    Bulunamadı
                  </motion.p>
                </div>
              </div>
              
              {/* Decorative outer ring */}
              <motion.div 
                animate={{ rotate: 360 }}
                transition={{ duration: 120, repeat: Infinity, ease: 'linear' }}
                className={`absolute inset-0 rounded-full -m-6 border-2 border-dashed ${
                  isDark ? 'border-gray-700' : 'border-gray-300'
                }`}
                style={{ width: 'calc(100% + 3rem)', height: 'calc(100% + 3rem)', top: '-1.5rem', left: '-1.5rem' }}
              />
              
              {/* Small decorative circle 1 */}
              <motion.div 
                animate={{ 
                  x: [0, 10, -5, 0],
                  y: [0, -10, 5, 0]
                }}
                transition={{ duration: 15, repeat: Infinity, repeatType: 'loop' }}
                className={`absolute w-12 h-12 rounded-full ${
                  isDark ? 'bg-blue-500/20' : 'bg-blue-100'
                } -top-6 right-8 shadow-lg backdrop-blur-sm border ${
                  isDark ? 'border-blue-500/30' : 'border-blue-200'
                }`}
              />
              
              {/* Small decorative circle 2 */}
              <motion.div 
                animate={{ 
                  x: [0, -8, 4, 0],
                  y: [0, 8, -4, 0]
                }}
                transition={{ duration: 12, repeat: Infinity, repeatType: 'loop', delay: 1 }}
                className={`absolute w-8 h-8 rounded-full ${
                  isDark ? 'bg-indigo-500/20' : 'bg-indigo-100'
                } bottom-4 -left-4 shadow-lg backdrop-blur-sm border ${
                  isDark ? 'border-indigo-500/30' : 'border-indigo-200'
                }`}
              />
            </div>
          </motion.div>

          {/* Support card - hidden on mobile, visible on desktop */}
          <motion.div
            variants={itemVariants}
            className={`hidden lg:block w-full max-w-md p-8 rounded-xl ${
              isDark 
                ? 'bg-gradient-to-br from-gray-800/70 to-gray-900/70 backdrop-blur-lg border border-gray-700 shadow-2xl' 
                : 'bg-white/80 backdrop-blur-lg border border-gray-100 shadow-xl'
            }`}
          >
            <h3 className={`text-xl font-bold mb-5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Yardıma mı ihtiyacınız var?
            </h3>
            
            <div className="space-y-4 mb-6">
              <div className={`p-4 rounded-lg ${
                isDark ? 'bg-gray-800 hover:bg-gray-700/70' : 'bg-gray-50 hover:bg-gray-100'
              } transition-colors cursor-pointer group`}>
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${
                    isDark ? 'bg-gray-700' : 'bg-white'
                  } group-hover:scale-110 transition-transform`}>
                    <FileQuestion className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                  <div>
                    <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Destek Dokümanları</h4>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Yardım belgelerimize göz atın
                    </p>
                  </div>
                </div>
              </div>
              
              <div className={`p-4 rounded-lg ${
                isDark ? 'bg-gray-800 hover:bg-gray-700/70' : 'bg-gray-50 hover:bg-gray-100'
              } transition-colors cursor-pointer group`}>
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${
                    isDark ? 'bg-gray-700' : 'bg-white'
                  } group-hover:scale-110 transition-transform`}>
                    <ExternalLink className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                  <div>
                    <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Durum Sayfası</h4>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Sistem durumunu kontrol edin
                    </p>
                  </div>
                </div>
              </div>
              
              <div className={`p-4 rounded-lg ${
                isDark ? 'bg-gray-800 hover:bg-gray-700/70' : 'bg-gray-50 hover:bg-gray-100'
              } transition-colors cursor-pointer group`}>
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${
                    isDark ? 'bg-gray-700' : 'bg-white'
                  } group-hover:scale-110 transition-transform`}>
                    <Mail className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                  <div>
                    <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>İletişim</h4>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Destek ekibimizle iletişime geçin
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className={`text-center text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              <p>Sorun devam ederse: <a href="mailto:info@beetrax.net" className={`${isDark ? 'text-blue-400' : 'text-blue-600'} font-medium`}>info@beetrax.net</a></p>
            </div>
          </motion.div>
        </motion.div>
      </div>
      
      {/* Footer */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.6 }}
        className={`absolute bottom-4 left-0 right-0 text-center text-sm ${isDark ? 'text-gray-600' : 'text-gray-400'}`}
      >
        <p>
          © {new Date().getFullYear()} İş Takip Sistemi | Tüm hakları saklıdır | <a href="#" className={`${isDark ? 'text-gray-500 hover:text-gray-400' : 'text-gray-500 hover:text-gray-600'} transition-colors`}>Gizlilik Politikası</a>
        </p>
      </motion.div>
    </div>
  );
};

export default NotFound; 